// Support mode banner (CR_20260913 §4.2 step 7): always visible while a super admin works inside a customer
// account — who is being supported, as which role (CR_20260924 Part C), since / until when, why — with Change role,
// Extend (below the 8 h cap) and Exit.
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { SupportInfo } from '@fleetspoke/core/types';
import { useSession } from '../auth/session';
import { dateTime } from '../lib/time';
import { SwitchSupportRoleDialog } from './SwitchSupportRoleDialog';

export const SUPPORT_BAR_HEIGHT = 44;
const CAP_MS = 8 * 60 * 60 * 1000;
const WARN_MS = 5 * 60 * 1000;

function clock(iso: string, language: string, timeZone: string): string {
  // the day only matters when the session crosses midnight; dateTime shows "13 Sep, 10:02"
  return dateTime(iso, language, timeZone) ?? '';
}

export function SupportBanner({ support, timeZone }: { support: SupportInfo; timeZone: string }) {
  const { t, i18n } = useTranslation();
  const { extendSupport, endSupport } = useSession();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [switching, setSwitching] = useState(false);
  const role = t(`settings.users.role_${support.act_as ?? 'manager'}`);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(timer);
  }, []);

  const expires = new Date(support.expires_at).getTime();
  const left = expires - now;
  const warn = left <= WARN_MS;
  const canExtend = expires < new Date(support.started_at).getTime() + CAP_MS;

  const exit = async () => {
    setBusy(true);
    try {
      await endSupport('exit');
      void navigate(`/admin/companies/${support.company_id}?tab=support`, { replace: true });
    } finally {
      setBusy(false);
    }
  };
  const extend = async () => {
    setBusy(true);
    try {
      await extendSupport(60);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        height: SUPPORT_BAR_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: { xs: 1.5, sm: 2 },
        color: '#FFFFFF',
        bgcolor: warn ? 'error.main' : 'primary.main',
        overflow: 'hidden',
      }}
    >
      <SupportAgentIcon fontSize="small" sx={{ flex: 'none' }} />
      <Typography
        variant="body2"
        noWrap
        sx={{ flex: 1, minWidth: 0, fontWeight: 600 }}
        title={support.reason}
      >
        <Box component="span" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em', mr: 1 }}>
          {t('support.mode')}
        </Box>
        {support.company_name}
        <Box component="span" data-testid="support-banner-role">
          {' · '}
          {t('support.acting_as', { role })}
        </Box>
        <Box component="span" sx={{ fontWeight: 400, display: { xs: 'none', md: 'inline' } }}>
          {' · '}
          {t('support.banner_since', { time: clock(support.started_at, i18n.language, timeZone) })}
        </Box>
        <Box component="span" sx={{ fontWeight: 400 }}>
          {' · '}
          {warn
            ? t('support.ending_soon', { minutes: Math.max(Math.ceil(left / 60_000), 0) })
            : t('support.banner_ends', {
                time: clock(support.expires_at, i18n.language, timeZone),
              })}
        </Box>
        <Box component="span" sx={{ fontWeight: 400, display: { xs: 'none', lg: 'inline' } }}>
          {' · '}“{support.reason}”
        </Box>
      </Typography>
      <Button
        size="small"
        color="inherit"
        variant="outlined"
        onClick={() => setSwitching(true)}
        disabled={busy}
        sx={{ display: { xs: 'none', sm: 'inline-flex' }, whiteSpace: 'nowrap' }}
        data-testid="support-change-role"
      >
        {t('support.switch_role')}
      </Button>
      {canExtend ? (
        <Tooltip title={t('support.extend_help')}>
          <span>
            <Button
              size="small"
              color="inherit"
              variant="outlined"
              onClick={() => void extend()}
              disabled={busy}
              sx={{ display: { xs: 'none', sm: 'inline-flex' }, whiteSpace: 'nowrap' }}
            >
              {t('support.extend')}
            </Button>
          </span>
        </Tooltip>
      ) : null}
      <Button
        size="small"
        variant="contained"
        onClick={() => void exit()}
        disabled={busy}
        sx={{
          whiteSpace: 'nowrap',
          bgcolor: '#FFFFFF',
          color: warn ? 'error.main' : 'primary.dark',
          '&:hover': { bgcolor: '#F3F4F2' },
        }}
      >
        {t('support.exit')}
      </Button>
      {switching ? (
        <SwitchSupportRoleDialog support={support} onClose={() => setSwitching(false)} />
      ) : null}
    </Box>
  );
}
